/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Industries — Interchange shared utilities (window.IFX)
 * assets/interchange-utils.js
 *
 * Loaded (deferred) BEFORE interchange-search.js and bearing-xref-mini.js
 * by their Liquid snippets. Exposes exactly one global: window.IFX.
 *
 *   IFX.escapeHtml(value)          → HTML-entity-escaped string
 *   IFX.escapeAttr(value)          → attribute-safe escaped string
 *   IFX.debounce(fn, ms)           → debounced wrapper (with .cancel())
 *   IFX.formatMoney(cents)         → "$12.34" (thousands-separated), "" if NaN
 *   IFX.showToast(message, opts)   → toast in .interchange-search__toasts
 *                                    opts: { tone: 'ok'|'err',
 *                                            linkHref, linkLabel } (optional)
 *                                    auto-dismiss 5 s, dismiss button,
 *                                    max 3 stacked (oldest evicted)
 *   IFX.track(name, payload)       → dataLayer.push({event:name, ...payload})
 *                                    + document CustomEvent(name, {detail})
 *                                    Never throws.
 *
 * Vanilla ES2019+, IIFE, no modules. Idempotent: both the full tool and the
 * mini widget include this file, so a second execution is a no-op.
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  if (window.IFX) return; // already installed by another snippet on the page

  var TOAST_DURATION_MS = 5000;
  var TOAST_LEAVE_MS = 260; // matches --ifx-dur-base + margin
  var TOAST_MAX = 3;

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeAttr(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function debounce(fn, ms) {
    var timer = null;
    function debounced() {
      var self = this;
      var args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(self, args);
      }, ms);
    }
    debounced.cancel = function () {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  function formatMoney(cents) {
    var n = Number(cents);
    if (!Number.isFinite(n)) return "";
    var dollars = n / 100;
    try {
      return "$" + dollars.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (err) {
      return "$" + dollars.toFixed(2);
    }
  }

  function getToastHost() {
    var host = document.querySelector(".interchange-search__toasts");
    if (host) return host;
    // Degraded fallback (page without the full tool): create a container so
    // toasts still function. Class rules apply if interchange-search.css is
    // loaded; otherwise the toast is unstyled but readable.
    host = document.createElement("div");
    host.className = "interchange-search__toasts";
    host.setAttribute("role", "status");
    host.setAttribute("aria-live", "polite");
    (document.body || document.documentElement).appendChild(host);
    return host;
  }

  function showToast(message, opts) {
    try {
      opts = opts || {};
      var host = getToastHost();
      if (!host) return null;

      // Max 3 stacked — evict the oldest immediately (no animation; it is
      // being replaced, not dismissed).
      while (host.children.length >= TOAST_MAX) {
        host.removeChild(host.firstElementChild);
      }

      var tone = opts.tone === "err" ? "error" : "success";
      var toast = document.createElement("div");
      toast.className = "interchange-search__toast interchange-search__toast--" + tone;

      var msg = document.createElement("span");
      msg.className = "interchange-search__toast-msg";
      msg.textContent = String(message == null ? "" : message);
      toast.appendChild(msg);

      if (opts.linkHref) {
        var link = document.createElement("a");
        link.className = "interchange-search__toast-link";
        link.href = String(opts.linkHref);
        link.textContent = String(opts.linkLabel || "View");
        toast.appendChild(link);
      }

      var closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "interchange-search__toast-close";
      closeBtn.setAttribute("aria-label", "Dismiss notification");
      closeBtn.innerHTML = "&times;";
      toast.appendChild(closeBtn);

      var dismissed = false;
      var autoTimer = null;
      function dismiss() {
        if (dismissed) return;
        dismissed = true;
        if (autoTimer) clearTimeout(autoTimer);
        toast.classList.add("is-leaving");
        setTimeout(function () {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, TOAST_LEAVE_MS);
      }
      closeBtn.addEventListener("click", dismiss);
      autoTimer = setTimeout(dismiss, TOAST_DURATION_MS);

      host.appendChild(toast);
      return toast;
    } catch (err) {
      try { console.error("[IFX] showToast failed", err); } catch (ignore) { /* noop */ }
      return null;
    }
  }

  function track(name, payload) {
    try {
      var dl = (window.dataLayer = window.dataLayer || []);
      dl.push(Object.assign({ event: name }, payload || {}));
    } catch (err) { /* never throws */ }
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload || {} }));
    } catch (err) { /* never throws */ }
  }

  window.IFX = {
    escapeHtml: escapeHtml,
    escapeAttr: escapeAttr,
    debounce: debounce,
    formatMoney: formatMoney,
    showToast: showToast,
    track: track,
  };
})();
